import { Link } from "react-router-dom";
import { Eye } from "lucide-react";
import { truncateText } from "../../utils/listFilterQuery";
import {
  REQUIREMENTS_PREVIEW_LENGTH,
  formatOfferValue,
  offerLinkLabel,
  offerStatusBadge,
} from "./offerListDisplay";

export type OfferListItem = {
  id: number;
  author?: { id: number; first_name: string };
  client?: { id: number; first_name: string };
  property_id: number;
  status: number;
  updated_at: string;
  offer_value?: string | number | null;
  requirements?: string | null;
};

const HEADER_CELL =
  "whitespace-nowrap border-x-0 border-b-0 border-l-0 border-r-0 border-t-0 px-1.5 py-2 text-left text-xs font-semibold text-slate-700";
const BODY_CELL =
  "border-x-0 border-b-0 border-l-0 border-r-0 border-t-0 px-1.5 py-2 text-[13px] text-slate-600";

export function OffersListTable({ offers }: { offers: OfferListItem[] }) {
  return (
    <div className="relative mt-4 overflow-x-auto overflow-hidden rounded-t-xl">
      <table className="min-w-[980px] w-full border-separate border-spacing-0 border-none text-[13px] font-[inherit]">
        <thead className="bg-slate-50">
          <tr>
            <th className={`${HEADER_CELL} rounded-tl-xl`}>Imóvel</th>
            <th className={HEADER_CELL}>Autor da proposta</th>
            <th className={HEADER_CELL}>Valor</th>
            <th className={HEADER_CELL}>Solicitações</th>
            <th className={HEADER_CELL}>Última atualização</th>
            <th className={HEADER_CELL}>Status</th>
            <th className={`${HEADER_CELL} rounded-tr-xl`}>Ações</th>
          </tr>
        </thead>
        <tbody>
          {offers.map((offer, rowIndex) => {
            const requirements = String(offer.requirements ?? "").trim();
            const badge = offerStatusBadge(offer.status);
            return (
              <tr
                key={offer.id}
                className={`transition ${
                  rowIndex % 2 === 0
                    ? "bg-white hover:bg-slate-50"
                    : "bg-slate-50/90 hover:bg-slate-100/90"
                }`}
              >
                <td className={`${BODY_CELL} whitespace-nowrap font-medium text-slate-700`}>
                  #{offer.property_id}
                </td>
                <td className={BODY_CELL}>
                  {offer.author?.first_name ?? offer.client?.first_name ?? "—"}
                </td>
                <td className={`${BODY_CELL} whitespace-nowrap font-semibold text-slate-700`}>
                  {formatOfferValue(offer.offer_value)}
                </td>
                <td className={`${BODY_CELL} max-w-[240px]`}>
                  {requirements ? (
                    <span className="line-clamp-2" title={requirements}>
                      {truncateText(requirements, REQUIREMENTS_PREVIEW_LENGTH)}
                    </span>
                  ) : (
                    "—"
                  )}
                </td>
                <td className={`${BODY_CELL} whitespace-nowrap`}>
                  {new Date(offer.updated_at).toLocaleString("pt-br")}
                </td>
                <td className={`${BODY_CELL} whitespace-nowrap`}>
                  <span
                    className={`inline-flex w-fit items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-semibold leading-tight ${badge.className}`}
                  >
                    {badge.label}
                  </span>
                </td>
                <td className={`${BODY_CELL} whitespace-nowrap`}>
                  <Link
                    to={`/see-offer/${String(offer.id)}`}
                    title={offerLinkLabel(offer.status)}
                    className="inline-flex rounded-lg p-1.5 text-sky-500 transition hover:bg-sky-50"
                  >
                    <Eye className="h-4 w-4" />
                  </Link>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
